/*
 * Kurse (Kursverwaltung, KI-Kurs): Rechnereien, die mehrere Ansichten teilen
 * und die sich ohne Browser prüfen lassen.
 *
 * Was ein Kurs ist und wer ihn sieht, entscheidet das Backend
 * (`services/courses`). Hier steht nur, wie die Oberfläche Zeiten anzeigt,
 * wann sie den Fortschritt meldet und wie sie Antworten sammelt.
 */

import { API_URL } from './config'

/**
 * Medienadressen (Video, Vorschau- und Titelbild) kommen vom Server als Pfad
 * („/api/public/course-media/…?s=…"). Ein `<video src>` oder ein
 * Hintergrundbild löst einen Pfad gegen die Adresse des **Frontends** auf —
 * das Backend liegt aber woanders. Deshalb kommt die Adresse des Backends
 * davor; volle Adressen (etwa später von Bunny) bleiben, wie sie sind.
 */
export const mediaUrl = (url, base = API_URL) => {
	if (!url) return null
	if (/^(https?:)?\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) return url
	return `${String(base || '').replace(/\/+$/, '')}${url.startsWith('/') ? '' : '/'}${url}`
}

/** Sekunden als „4:54" (unter einer Stunde) bzw. „1:02:05". */
export const formatClock = sekunden => {
	const s = Math.max(0, Math.round(Number(sekunden) || 0))
	const h = Math.floor(s / 3600)
	const m = Math.floor((s % 3600) / 60)
	const rest = String(s % 60).padStart(2, '0')

	return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${rest}` : `${m}:${rest}`
}

/**
 * Dauer in Worten, gerundet auf Minuten: „5 Min.", „1 Std. 14 Min.".
 * Unter einer Minute zählt als eine — „0 Min." wäre eine Lektion ohne Inhalt.
 */
export const formatMinutes = (sekunden, t = text => text) => {
	const minuten = Math.max(1, Math.round((Number(sekunden) || 0) / 60))
	const h = Math.floor(minuten / 60)
	const m = minuten % 60

	if (h === 0) return t('{{m}} min', { m })
	if (m === 0) return t('{{h}} h', { h })
	return t('{{h}} h {{m}} min', { h, m })
}

/** Anteil gesehen in Prozent (0–100), für den Balken unter der Kachel. */
export const watchedPercent = (maxPosition, duration) => {
	const d = Number(duration) || 0
	if (d <= 0) return 0
	return Math.min(100, Math.max(0, Math.round(((Number(maxPosition) || 0) / d) * 100)))
}

export const PROGRESS_INTERVAL_MS = 10000

/**
 * Meldet den Fortschritt einer Videolektion — höchstens alle zehn Sekunden.
 *
 * Während der Wiedergabe kommt `timeupdate` mehrmals pro Sekunde. Jede davon
 * an den Server zu schicken hieße bei 25 Geräten in einer Klasse hunderte
 * Anfragen pro Minute für eine Zahl, die sich kaum bewegt hat. Bei Pause und
 * Ende wird dagegen sofort gemeldet: Dann hört die Person auf, und was danach
 * nicht mehr ankommt, wäre verloren.
 *
 * `send` bekommt `{ position, duration, ended? }` und liefert ein Promise.
 * `now` ist austauschbar, damit sich der Takt im Test ohne Warten prüfen lässt.
 */
export const createProgressReporter = ({ send, intervalMs = PROGRESS_INTERVAL_MS, now = () => Date.now() }) => {
	let zuletzt = null
	let zuletztPosition = -1

	const melden = payload => {
		zuletzt = now()
		zuletztPosition = payload.position
		return Promise.resolve(send(payload)).catch(() => null)
	}

	return {
		/** Bei `timeupdate`: nur, wenn das letzte Melden lange genug her ist. */
		tick(position, duration) {
			if (!(duration > 0)) return null
			if (zuletzt !== null && now() - zuletzt < intervalMs) return null
			if (Math.floor(position) === Math.floor(zuletztPosition)) return null
			return melden({ position: Math.floor(position), duration: Math.floor(duration) })
		},
		/** Bei Pause und beim Verlassen: sofort, sofern sich etwas bewegt hat. */
		flush(position, duration) {
			if (!(duration > 0)) return null
			if (Math.floor(position) === Math.floor(zuletztPosition)) return null
			return melden({ position: Math.floor(position), duration: Math.floor(duration) })
		},
		/** Am Ende: immer, mit `ended`, damit die Lektion sicher als gesehen gilt. */
		ended(duration) {
			return melden({ position: Math.floor(duration), duration: Math.floor(duration), ended: true })
		}
	}
}

/**
 * Eine Antwort an- oder abwählen. Bei Einfachauswahl ersetzt die neue die
 * alte; bei Mehrfachauswahl schaltet ein Klick um.
 */
export const toggleAnswer = (gewaehlt = [], answerId, multiple) => {
	if (!multiple) return [answerId]
	return gewaehlt.includes(answerId) ? gewaehlt.filter(id => id !== answerId) : [...gewaehlt, answerId]
}

/** Sind alle Fragen beantwortet? Erst dann lässt sich das Quiz abgeben. */
export const allAnswered = (questions = [], antworten = {}) =>
	questions.length > 0 && questions.every(frage => (antworten[frage.id] || []).length > 0)

/**
 * Die Dauer einer Videodatei, gemessen im Browser — bevor sie hochgeht.
 *
 * Der Server hat kein Werkzeug, um eine Videodatei zu vermessen; die Dauer
 * kommt deshalb als Feld neben der Datei mit. Gelesen werden nur die
 * Metadaten, nicht das ganze Video.
 */
export const measureVideoDuration = file =>
	new Promise(resolve => {
		const url = URL.createObjectURL(file)
		const video = document.createElement('video')
		const fertig = wert => {
			URL.revokeObjectURL(url)
			resolve(wert)
		}
		video.preload = 'metadata'
		video.onloadedmetadata = () => fertig(Number.isFinite(video.duration) ? Math.round(video.duration) : 0)
		video.onerror = () => fertig(0)
		video.src = url
	})

export const COMPANY_TYPE_KEYS = ['JC', 'JCC', 'JBC']

export const COMPANY_TYPE_LABELS = {
	JC: 'Junior Company',
	JCC: 'Junior Compact Company',
	JBC: 'Junior Basic Company'
}

/** Voreinstellung der Einstellung `learningCourses`, wenn noch keine gespeichert ist. */
export const LEARNING_COURSES_DEFAULTS = {
	enabled: false,
	creditEnabled: false,
	perCompletion: { JC: 10, JCC: 10, JBC: 10 },
	cap: { JC: 100, JCC: 100, JBC: 100 },
	reminderDays: 14
}

/** Betrag als „10,00 €". */
export const formatEuro = wert =>
	`${new Intl.NumberFormat('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
		Number(wert) || 0
	)} €`

/** Größe eines Teilstücks beim Hochladen: Der Webserver nimmt höchstens ~10 MB je Anfrage an. */
export const CHUNK_SIZE = 8 * 1024 * 1024

/** Eine Kennung für einen Hochladevorgang, wie sie der Server verlangt ([A-Za-z0-9-]{8,64}). */
export const newUploadId = () =>
	`up-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}${Math.random().toString(36).slice(2, 10)}`

/**
 * Ein Video in Teilstücken hochladen.
 *
 * Der Webserver vor dem Backend lehnt Anfragen über etwa 10 MB ab (413). Ein
 * Video von 20 MB geht deshalb in Stücken zu höchstens 8 MB hinaus — streng
 * nacheinander, jedes erst, wenn das vorige angekommen ist. Der Server setzt
 * sie zusammen und antwortet beim letzten Stück mit der fertigen Lektion.
 *
 * Scheitert ein Stück, beginnt der ganze Vorgang noch einmal von vorn, mit
 * neuer Kennung — ein halb zusammengesetztes Video ist auf dem Server nichts
 * wert. Nach `attempts` Anläufen gibt der Vorgang auf und reicht den Fehler weiter.
 *
 * `send(form, onChunkProgress)` schickt ein Stück (in der Ansicht: `http.post`)
 * und liefert die Antwort des Servers. `onProgress` bekommt 0–100.
 */
export const uploadInChunks = async ({
	file,
	duration,
	send,
	onProgress = () => {},
	chunkSize = CHUNK_SIZE,
	attempts = 2,
	makeId = newUploadId
}) => {
	const total = Math.max(1, Math.ceil(file.size / chunkSize))
	let letzterFehler = null

	for (let anlauf = 0; anlauf < attempts; anlauf += 1) {
		const uploadId = makeId()
		try {
			let antwort = null
			for (let index = 0; index < total; index += 1) {
				const stueck = file.slice(index * chunkSize, Math.min(file.size, (index + 1) * chunkSize))
				const form = new FormData()
				form.append('uploadId', uploadId)
				form.append('index', String(index))
				form.append('total', String(total))
				form.append('duration', String(duration || 0))
				form.append('file', stueck, file.name || 'video.mp4')

				onProgress(Math.round((index / total) * 100))
				// eslint-disable-next-line no-await-in-loop
				antwort = await send(form, anteil => onProgress(Math.round(((index + Math.min(1, anteil)) / total) * 100)))
			}
			onProgress(100)
			return antwort
		} catch (fehler) {
			letzterFehler = fehler
		}
	}

	throw letzterFehler
}
